import alt from 'components/Dispatcher';
import {Store, StoreMethods} from '@store/@types/alt';
import {Call} from './Call';
import CallCardActions, {CallCardActionsInterface} from './CallCardActions';
import {Vote} from 'records/CallDetails';
import Scorecard from 'records/Scorecard';
import {List, Map, OrderedMap} from 'immutable';
import {
  CallAsset,
  Segment,
  Speaker,
  SpeakerNumber,
  TopicMatch,
  TranscriptBlock,
} from '@store/call-card/CallCard.types';
import {UserCallActivity} from '@store/call-card/UserCallActivity';
import {FileUploadId} from 'records/CoachingReviewFileUpload';
import {Color, Seconds} from '@store/@types/util';
import {UserComment} from 'records/UserComment';
import {UserId} from 'records/User';
import {TopicId, TopicFilter, Topic} from '@store/topic-keyword/TopicKeyword.types';
import {getNextColor} from '@components/call-card/TopicKeyword.utils';
import {MiniSpeakerNumber} from '@components/call-card/CallSpeaker.utils';

interface CallCardStoreState {
  calls: Map<FileUploadId, Call>;
  callAssets: Map<FileUploadId, CallAsset>;
  scorecards: Map<FileUploadId, List<Scorecard>>;
  speakers: Map<FileUploadId, List<Speaker>>;
  userActivities: Map<FileUploadId, List<UserCallActivity>>;
  transcripts: Map<FileUploadId, List<TranscriptBlock>>;
  segments: Map<FileUploadId, List<Segment>>;
  /**
   * @link CallCardStore.getSegmentsSuccess
   */
  talkTimes: Map<FileUploadId, Map<SpeakerNumber, Seconds>>;
  comments: Map<FileUploadId, Map<UserId, List<UserComment>>>;

  // All topics/keywords
  topics: Map<FileUploadId, List<Topic>>;
  // Active ids for topics/keywords/freetext and their color/textColor
  activeTopics: Map<FileUploadId, OrderedMap<TopicId, Color>>;
  // Matches for all keyword/topics
  matches: Map<FileUploadId, Map<TopicId, List<TopicMatch>>>;
  topicFilters: Map<FileUploadId, TopicFilter>;
}

class CallCardStore
  extends Store<CallCardStoreState>
  implements StoreMethods<CallCardActionsInterface>
{
  constructor() {
    super();
    this.state = {
      calls: Map(),
      callAssets: Map(),
      scorecards: Map(),
      speakers: Map(),
      userActivities: Map(),
      transcripts: Map(),
      segments: Map(),
      talkTimes: Map(),
      comments: Map(),
      topics: Map(),
      activeTopics: Map(),
      matches: Map(),
      topicFilters: Map(),
    };
    this.bindActions(CallCardActions);
  }

  getCall(): void {}
  getCallError(error: Error): void {
    console.error(error);
  }
  getCallSuccess(call: Call): void {
    this.setState({
      calls: this.state.calls.set(call.id, call),
    });
  }

  getCallSource(): void {}
  getCallSourceError(error: Error): void {
    console.error(error);
  }
  getCallSourceSuccess([assets, callId]: [CallAsset[], FileUploadId]): void {
    const video = assets.find(a => a.type === 'OUTPUT_VIDEO');
    const audio = assets.find(a => a.type === 'OUTPUT_AUDIO');
    const asset = video ?? audio ?? {id: '', type: 'MISSING_ASSET'};
    this.setState({callAssets: this.state.callAssets.set(callId, asset)});
  }

  getScorecards(): void {}
  getScorecardsError(error: Error): void {
    console.error(error);
  }
  getScorecardsSuccess([scorecards, callId]: [List<Scorecard>, FileUploadId]): void {
    this.setState({scorecards: this.state.scorecards.set(callId, scorecards)});
  }

  getSpeakers(): void {}
  getSpeakersError(error: Error): void {
    console.error(error);
  }
  getSpeakersSuccess([speakers, callId]: [List<Speaker>, FileUploadId]): void {
    this.setState({speakers: this.state.speakers.set(callId, speakers)});
  }

  getUserCallActivity(): void {}
  getUserCallActivityError(error: Error): void {
    console.error(error);
  }
  getUserCallActivitySuccess([userActivities, callId]: [
    List<UserCallActivity>,
    FileUploadId
  ]): void {
    this.setState({userActivities: this.state.userActivities.set(callId, userActivities)});
  }

  getTranscript(): void {}
  getTranscriptError(error: Error): void {
    console.error(error);
  }
  getTranscriptSuccess([transcript, callId]: [List<TranscriptBlock>, FileUploadId]): void {
    this.setState({transcripts: this.state.transcripts.set(callId, transcript)});
  }

  getSegments(): void {}
  getSegmentsError(error: Error): void {
    console.error(error);
  }
  getSegmentsSuccess([segments, callId]: [List<Segment>, FileUploadId]): void {
    const talkTimes = segments.reduce(
      // Sum speaker times by speaker number
      (acc, val) =>
        acc
          .update(val.speakerNumber, (time = 0) => time + (val.endTime - val.startTime))
          .update(MiniSpeakerNumber, (time = 0) => time + (val.endTime - val.startTime)),
      Map<number, Seconds>()
    );
    this.setState({
      segments: this.state.segments.set(callId, segments),
      talkTimes: this.state.talkTimes.set(callId, talkTimes),
    });
  }

  getComments(): void {}
  getCommentsError(error: Error): void {
    console.error(error);
  }
  getCommentsSuccess([comments, callId]: [List<UserComment>, FileUploadId]): void {
    this.setState({
      comments: this.state.comments.set(
        callId,
        comments.groupBy(c => c.userId)
      ),
    });
  }

  setVote([callId, vote]: [FileUploadId, Vote]): void {
    const call = this.state.calls.get(callId);
    if (!call) return;
    if (vote === 'Up') {
      const diff = call.vote === 'Down' ? 1 : 0;
      const updatedCall = call
        .update('votesUp', (votes = 1) => votes + 1)
        .update('votesDown', (votes = 1) => votes - diff)
        .set('vote', vote);
      this.setState({
        calls: this.state.calls.set(callId, updatedCall),
      });
    } else {
      const diff = call.vote === 'Up' ? 1 : 0;
      const updatedCall = call
        .update('votesUp', (votes = 1) => votes - diff)
        .update('votesDown', (votes = 1) => votes + 1)
        .set('vote', vote);
      this.setState({
        calls: this.state.calls.set(callId, updatedCall),
      });
    }
  }
  setVoteError(error: Error): void {
    console.error(error);
  }
  setVoteSuccess(): void {}

  deleteVote(callId: FileUploadId): void {
    const call = this.state.calls.get(callId);
    if (!call) return;
    const updatedCall = call
      .update(call.vote === 'Up' ? 'votesUp' : 'votesDown', (votes = 1) => votes - 1)
      .set('vote', undefined);
    this.setState({
      calls: this.state.calls.set(callId, updatedCall),
    });
  }
  deleteVoteError(error: Error): void {
    console.error(error);
  }
  deleteVoteSuccess(): void {}

  setCoachMe(): void {}
  setCoachMeError(error: Error): void {
    console.error(error);
  }
  setCoachMeSuccess([coachMe, callId]: [boolean, FileUploadId]): void {
    this.setState({
      calls: this.state.calls.update(callId, call => call.set('coachMe', coachMe)),
    });
  }

  getMatches(): void {}
  getMatchesError(error: Error): void {
    console.error(error);
  }
  getMatchesSuccess([newMatches, callId]: [List<TopicMatch>, FileUploadId]): void {
    if (newMatches.isEmpty()) return;

    const id = newMatches.first().id;
    this.setState({
      matches: this.state.matches.update(callId, (matches = Map()) => matches.set(id, newMatches)),
    });
  }

  getTopicFilter(): void {}
  setTopicFilter([callId, filter]: [FileUploadId, TopicFilter]): void {
    this.setState({
      topicFilters: this.state.topicFilters.update(callId, () => filter),
    });
  }

  toggleTopic([callId, topicId]: [FileUploadId, TopicId]): void {
    this.setState({
      activeTopics: this.state.activeTopics.update(callId, (actives = OrderedMap()) =>
        actives.has(topicId) ? actives.delete(topicId) : actives.set(topicId, getNextColor(actives))
      ),
    });
  }

  resetMatches() {
    this.setState({
      matches: Map(),
      activeTopics: Map(),
    });
  }

  getTopics(): void {}
  getTopicsError(error: Error): void {
    console.error(error);
  }
  getTopicsSuccess([topics, extraTopicIds, callId]: [
    List<Topic>,
    List<TopicId>,
    FileUploadId
  ]): void {
    this.setState({
      topics: this.state.topics.set(
        callId,
        // sort the "extra" topics up to the top. we want to
        // make them stand out since they're special
        List([
          ...topics.filter(t => extraTopicIds.contains(t.id)),
          ...topics.filter(t => !extraTopicIds.contains(t.id)),
        ])
      ),
    });
  }

  resetTopics(callId) {
    this.setState({
      topics: this.state.topics.set(callId, List()),
    });
  }
}

export default alt.createStore(CallCardStore);
